import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const URL_MAX = 2000;
const PSI_TIMEOUT_MS = 90_000;
const PSI_RETRY_DELAYS_MS = [0, 1_500, 4_000];
const PRIVATE_HOST_RE =
  /^(localhost|127\.|10\.|192\.168\.|169\.254\.|0\.0\.0\.0|::1|\[::1\]|fc00:|fd[0-9a-f]{2}:|fe80:)/i;

const InputSchema = z.object({
  url: z.string().min(1).max(URL_MAX),
  locale: z.enum(["de", "en"]),
});

function validateUrl(raw: string): string {
  const trimmed = raw.trim();
  let parsed: URL;
  try {
    parsed = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
  } catch {
    throw new Error("invalid_url");
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error("invalid_scheme");
  }
  if (PRIVATE_HOST_RE.test(parsed.hostname)) throw new Error("private_host");
  if (!parsed.hostname.includes(".")) throw new Error("private_host");
  if (/^\d+\.\d+\.\d+\.\d+$/.test(parsed.hostname)) throw new Error("private_host");
  return parsed.toString();
}

export type LighthouseScore = { label: string; value: number };
export type LighthouseVital = {
  abbr: string;
  label: string;
  value: string;
  color: "green" | "orange" | "red";
};
export type LighthouseRecommendation = {
  id: string;
  title: string;
  description?: string;
  savingsMs?: number;
  savingsBytes?: number;
};
export type LighthouseAuditResult = {
  url: string;
  fetchedAt: string;
  scores: LighthouseScore[];
  vitals: LighthouseVital[];
  recommendations: LighthouseRecommendation[];
};

type PsiCategory = { score: number | null };
type PsiAuditDetails = {
  type?: string;
  overallSavingsMs?: number;
  overallSavingsBytes?: number;
};
type PsiAudit = {
  score?: number | null;
  displayValue?: string;
  title?: string;
  description?: string;
  details?: PsiAuditDetails;
};
type PsiResponse = {
  lighthouseResult?: {
    fetchTime?: string;
    categories?: Record<string, PsiCategory | undefined>;
    audits?: Record<string, PsiAudit | undefined>;
  };
};

function scoreOf(category: PsiCategory | undefined): number {
  if (!category || category.score == null) return 0;
  return Math.round(category.score * 100);
}

function colorForVital(score: number | null | undefined): "green" | "orange" | "red" {
  if (score == null) return "orange";
  if (score >= 0.9) return "green";
  if (score >= 0.5) return "orange";
  return "red";
}

function stripMarkdownLinks(s: string): string {
  return s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").trim();
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchPsiWithRetry(url: string): Promise<Response> {
  let lastError: unknown;

  for (const [attempt, delay] of PSI_RETRY_DELAYS_MS.entries()) {
    if (delay > 0) await wait(delay);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PSI_TIMEOUT_MS);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (response.ok) return response;

      const shouldRetry = response.status === 429 || response.status >= 500;
      const body = await response.text().catch(() => "");
      console.error("PSI API error", response.status, `attempt=${attempt + 1}`, body.slice(0, 500));

      if (!shouldRetry || attempt === PSI_RETRY_DELAYS_MS.length - 1) {
        throw new Error(`PSI request failed (${response.status})`);
      }
    } catch (error) {
      lastError = error;
      const isAbort = error instanceof Error && error.name === "AbortError";
      console.error("PSI fetch failed", `attempt=${attempt + 1}`, isAbort ? "timeout" : error);

      if (attempt === PSI_RETRY_DELAYS_MS.length - 1) {
        throw isAbort ? new Error("PSI request timed out") : error;
      }
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError instanceof Error ? lastError : new Error("PSI request failed");
}

export const runLighthouseAudit = createServerFn({ method: "POST" })
  .inputValidator((input) => InputSchema.parse(input))
  .handler(async ({ data }): Promise<LighthouseAuditResult> => {
    const apiKey = process.env.PSI_API_KEY;
    if (!apiKey) {
      throw new Error("PSI_API_KEY not configured");
    }

    const safeUrl = validateUrl(data.url);

    const params = new URLSearchParams();
    params.set("url", safeUrl);
    params.set("key", apiKey);
    params.set("strategy", "mobile");
    for (const cat of ["performance", "accessibility", "best-practices", "seo"]) {
      params.append("category", cat);
    }
    params.set("locale", data.locale === "de" ? "de" : "en");

    const res = await fetchPsiWithRetry(
      `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?${params.toString()}`,
    );
    const json = (await res.json()) as PsiResponse;
    const lh = json?.lighthouseResult ?? {};
    const cat: Record<string, PsiCategory | undefined> = lh.categories ?? {};
    const audits: Record<string, PsiAudit | undefined> = lh.audits ?? {};
    const loc = data.locale;

    const scores: LighthouseScore[] = [
      { label: loc === "de" ? "Leistung" : "Performance", value: scoreOf(cat.performance) },
      {
        label: loc === "de" ? "Barrierefreiheit" : "Accessibility",
        value: scoreOf(cat.accessibility),
      },
      { label: "Best Practices", value: scoreOf(cat["best-practices"]) },
      { label: "SEO", value: scoreOf(cat.seo) },
    ];

    const vitalDef = [
      { key: "first-contentful-paint", abbr: "FCP", label: "First Contentful Paint" },
      { key: "largest-contentful-paint", abbr: "LCP", label: "Largest Contentful Paint" },
      { key: "total-blocking-time", abbr: "TBT", label: "Total Blocking Time" },
      { key: "cumulative-layout-shift", abbr: "CLS", label: "Cumulative Layout Shift" },
    ];

    const vitals: LighthouseVital[] = vitalDef.map((v) => {
      const a = audits[v.key];
      return {
        abbr: v.abbr,
        label: v.label,
        value: a?.displayValue ?? "—",
        color: colorForVital(a?.score),
      };
    });

    const recommendations: LighthouseRecommendation[] = Object.entries(audits)
      .map(([id, a]) => {
        if (!a) return null;
        const det = a?.details;
        if (!det || det.type !== "opportunity") return null;
        const ms = typeof det.overallSavingsMs === "number" ? det.overallSavingsMs : 0;
        const bytes = typeof det.overallSavingsBytes === "number" ? det.overallSavingsBytes : 0;
        if (ms <= 50 && bytes <= 1024) return null;
        return {
          id,
          title: a.title ?? id,
          description:
            typeof a.description === "string" ? stripMarkdownLinks(a.description) : undefined,
          savingsMs: ms,
          savingsBytes: bytes,
        } as LighthouseRecommendation;
      })
      .filter((x): x is LighthouseRecommendation => x !== null)
      .sort((a, b) => (b.savingsMs ?? 0) - (a.savingsMs ?? 0))
      .slice(0, 5);

    return {
      url: safeUrl,
      fetchedAt: lh.fetchTime ?? new Date().toISOString(),
      scores,
      vitals,
      recommendations,
    };
  });
